import { newOperationId } from '../../../shared/model/operationIds'
import { normalizeRid } from '../../../shared/model/scopeKeys'

/**
 * Байты выбранного снимка — только в памяти страницы, не в Redux и не в
 * хранилище: Blob несериализуем, а снимок — не намерение, а груз команды.
 * Держится, пока исход загрузки не стал известен (для «Проверить исход» нужны
 * те же байты); после итога удаляется. Рядом живёт превью (`blob:`-адрес
 * исходного снимка) и управление отменой отправки.
 */
interface Stash {
  resourceId: string
  file: Blob
  /** Адрес превью исходного снимка; `null` — окружение не умеет (тесты) или не вышло. */
  previewUrl: string | null
  /** Прерывает идущую отправку; есть только пока она идёт. */
  abort: AbortController | null
}

const files = new Map<string, Stash>()

/** Не больше этого числа снимков в памяти: брошенные загрузки не копятся. */
const MAX_FILES = 10

function makePreviewUrl(file: Blob): string | null {
  try {
    return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null
  } catch {
    return null
  }
}

function release(entry: Stash | undefined): void {
  if (entry === undefined) return
  if (entry.previewUrl !== null) {
    try {
      URL.revokeObjectURL(entry.previewUrl)
    } catch {
      // Окружение без revokeObjectURL: превью просто останется до закрытия страницы.
    }
  }
}

export function stashPhotoFile(resourceId: string, file: Blob): string {
  while (files.size >= MAX_FILES) {
    const oldest = files.keys().next().value
    if (oldest === undefined) break
    release(files.get(oldest))
    files.delete(oldest)
  }
  const token = newOperationId()
  files.set(token, { resourceId: normalizeRid(resourceId), file, previewUrl: makePreviewUrl(file), abort: null })
  return token
}

export const photoFile = (token: string): Blob | undefined => files.get(token)?.file

/** Превью исходного снимка; сжатый вариант на экране не показывается — человек видит то, что снял. */
export const photoPreviewUrl = (token: string | null): string | null => (token === null ? null : (files.get(token)?.previewUrl ?? null))

/** Подготовленный (сжатый) снимок заменяет исходный: повтор и «Проверить исход» идут теми же байтами. Превью остаётся прежним. */
export function replacePhotoFile(token: string, file: Blob): void {
  const entry = files.get(token)
  if (entry !== undefined) entry.file = file
}

/** Начинает отправку: возвращает сигнал для отмены. Прежняя отправка этого снимка, если была, больше не нужна. */
export function beginPhotoSend(token: string): AbortSignal | undefined {
  const entry = files.get(token)
  if (entry === undefined) return undefined
  entry.abort = new AbortController()
  return entry.abort.signal
}

/** Отправка кончилась (ответ, сбой, отмена): управление отменой больше не нужно. */
export function endPhotoSend(token: string): void {
  const entry = files.get(token)
  if (entry !== undefined) entry.abort = null
}

/** Отменить идущую отправку. `true` — было что отменять. */
export function abortPhotoSend(token: string): boolean {
  const entry = files.get(token)
  if (entry?.abort == null) return false
  entry.abort.abort()
  return true
}

export function dropPhotoFile(token: string): void {
  release(files.get(token))
  files.delete(token)
}

/** Выход/смена пользователя: чужие снимки не остаются в памяти. */
export function dropAllPhotoFiles(): void {
  for (const entry of files.values()) {
    entry.abort?.abort()
    release(entry)
  }
  files.clear()
}

/** Ресурс вышел из работы: его снимки уходят, соседние остаются. */
export function dropPhotoFilesOf(resourceId: string): void {
  const rid = normalizeRid(resourceId)
  for (const [token, entry] of files) {
    if (entry.resourceId !== rid) continue
    entry.abort?.abort()
    release(entry)
    files.delete(token)
  }
}
